// src/components/AdminPanel.js
import React, { useState, useEffect } from 'react';
import {
  collection, doc, setDoc, deleteDoc, getDocs, getDoc,
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import { db, auth } from '../firebase/config';
import { useAuth } from '../AuthContext';

export default function AdminPanel({ onClose }) {
  const { userProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  async function load() {
    const usersSnap = await getDocs(collection(db, 'users'));
    setUsers(usersSnap.docs.map(d => ({ uid: d.id, ...d.data() })));
    const allowedSnap = await getDocs(collection(db, 'allowedUsers'));
    setAllowed(allowedSnap.docs.map(d => ({ email: d.id, ...d.data() })));
  }

  useEffect(() => { load(); }, []);

  async function addUser() {
    setErr(''); setMsg('');
    if (!newEmail || !newPass || !newName) { setErr('All fields required.'); return; }
    setAdding(true);
    try {
      // Create Firebase auth user
      const cred = await createUserWithEmailAndPassword(auth, newEmail.trim(), newPass);
      const uid = cred.user.uid;
      // Add to allowedUsers collection
      await setDoc(doc(db, 'allowedUsers', newEmail.trim().toLowerCase()), {
        role: newRole,
        addedAt: Date.now(),
        addedBy: userProfile?.email || '',
      });
      // Create user profile
      await setDoc(doc(db, 'users', uid), {
        email: newEmail.trim().toLowerCase(),
        displayName: newName.trim(),
        role: newRole,
        createdAt: Date.now(),
      });
      setNewEmail(''); setNewPass(''); setNewName(''); setNewRole('user');
      setMsg(`✓ User "${newName}" added successfully.`);
      await load();
    } catch (e) {
      if (e.code === 'auth/email-already-in-use') {
        // If auth user already exists, just add to allowed list
        await setDoc(doc(db, 'allowedUsers', newEmail.trim().toLowerCase()), {
          role: newRole,
          addedAt: Date.now(),
          addedBy: userProfile?.email || '',
        });
        setMsg('Email already had an auth account — added to allowed list.');
        await load();
      } else {
        setErr('Error: ' + e.message);
      }
    }
    setAdding(false);
  }

  async function removeFromAllowed(email) {
    if (!window.confirm(`Remove access for ${email}? They will not be able to log in.`)) return;
    await deleteDoc(doc(db, 'allowedUsers', email));
    await load();
    setMsg(`Removed access for ${email}`);
  }

  async function updateRole(uid, role) {
    await setDoc(doc(db, 'users', uid), { role }, { merge: true });
    const email = users.find(u => u.uid === uid)?.email;
    if (email) await setDoc(doc(db, 'allowedUsers', email.toLowerCase()), { role }, { merge: true });
    await load();
  }

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 300, padding: 20, overflowY: 'auto',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 620,
        border: '1px solid rgba(0,0,0,0.15)', margin: 'auto',
      }}>
        <div style={{
          padding: '13px 18px', borderBottom: '1px solid rgba(0,0,0,0.1)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ fontSize: 15, fontWeight: 800, flex: 1 }}>👥 Team Admin</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999' }}>✕</button>
        </div>

        <div style={{ padding: 18 }}>
          {msg && <div style={{ background: '#EAF3DE', color: '#27500A', padding: '8px 12px', borderRadius: 7, marginBottom: 12, fontSize: 13 }}>{msg}</div>}
          {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 7, marginBottom: 12, fontSize: 13 }}>{err}</div>}

          {/* Add new user */}
          <div style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, padding: 14, marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 12 }}>Add New Team Member</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#888', marginBottom: 4, textTransform: 'uppercase' }}>Display Name</div>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="Jane Smith"
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#888', marginBottom: 4, textTransform: 'uppercase' }}>Role</div>
                <select value={newRole} onChange={e => setNewRole(e.target.value)} style={inputStyle}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="pm">Project Manager</option>
                  <option value="viewer">Viewer (read-only)</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#888', marginBottom: 4, textTransform: 'uppercase' }}>Email</div>
                <input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="jane@company.com"
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#888', marginBottom: 4, textTransform: 'uppercase' }}>Temporary Password</div>
                <input
                  type="text"
                  value={newPass}
                  onChange={e => setNewPass(e.target.value)}
                  placeholder="Min 6 characters"
                  style={inputStyle}
                />
              </div>
            </div>
            <button
              onClick={addUser}
              disabled={adding}
              style={{
                marginTop: 12, fontSize: 12, padding: '7px 16px',
                background: adding ? '#aaa' : '#185FA5', color: '#fff',
                border: 'none', borderRadius: 7, cursor: adding ? 'not-allowed' : 'pointer',
                fontWeight: 700, fontFamily: 'inherit',
              }}
            >
              {adding ? 'Adding…' : '+ Add Team Member'}
            </button>
          </div>

          {/* Current users */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 10 }}>
              Team Members ({users.length})
            </div>
            {users.map(u => (
              <div key={u.uid} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', borderRadius: 7, marginBottom: 4,
                background: '#f8f8f8', border: '1px solid rgba(0,0,0,0.06)',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: '#185FA5', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, flexShrink: 0,
                }}>
                  {(u.displayName || u.email || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{u.displayName || u.email}</span>
                  {u.role === 'viewer' && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#f0f0f0', color: '#888', fontWeight: 700 }}>VIEWER</span>}
                  {u.role === 'pm' && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#EAF3DE', color: '#27500A', fontWeight: 700 }}>PM</span>}
                  {u.role === 'admin' && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#E6F1FB', color: '#185FA5', fontWeight: 700 }}>ADMIN</span>}
                  {u.role === 'supervisor' && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#EEEDFE', color: '#3C3489', fontWeight: 700 }}>SUPERVISOR</span>}
                </div>
                  <div style={{ fontSize: 11, color: '#888' }}>{u.email}</div>
                </div>
                <select
                  value={u.role || 'user'}
                  onChange={e => updateRole(u.uid, e.target.value)}
                  style={{ fontSize: 11, padding: '3px 6px', borderRadius: 5, border: '1px solid rgba(0,0,0,0.18)', background: '#fff' }}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="pm">Project Manager</option>
                  <option value="viewer">Viewer (read-only)</option>
                </select>
                {u.email && u.uid !== userProfile?.uid && (
                  <button
                    onClick={() => removeFromAllowed(u.email.toLowerCase())}
                    style={{ fontSize: 11, padding: '3px 8px', background: 'none', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 5, cursor: 'pointer', color: '#888' }}
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', fontSize: 13, padding: '6px 9px',
  border: '1px solid rgba(0,0,0,0.18)', borderRadius: 7,
  background: '#f5f5f3', color: '#1a1a18', fontFamily: 'inherit',
  boxSizing: 'border-box',
};
